import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { aggregateCashierRows, CASHIER_DETAIL_LIMIT, CASHIER_EXPORT_LIMIT } from '@/lib/reporting/cashierEngine';
import { parseCashierReportPayload } from '@/lib/reporting/cashierParse';
import type { CashierFilterState, CashierReportResult } from '@/lib/reporting/cashierTypes';
import { CLINIC_TIMEZONE, resolveReportPeriod, toIsoRange } from '@/lib/reporting/periods';

function rpc<T>(name: string, args: Record<string, unknown>) {
  return supabase.rpc(name as never, args as never) as unknown as Promise<{
    data: T | null;
    error: Error | null;
  }>;
}

export function cashierReportQueryKey(
  clinicId: string | null,
  range: { start: string; end: string },
  filters: CashierFilterState,
  detailLimit: number,
) {
  return ['cashier-report', clinicId, range, filters, detailLimit] as const;
}

function filtersToRpc(filters: CashierFilterState) {
  return {
    practitioner_ids: filters.practitionerIds,
    location_ids: filters.locationIds,
    payment_methods: filters.paymentMethods,
    channels: filters.channels,
    statuses: filters.statuses,
    search: filters.search.trim(),
  };
}

async function fetchCashierReport(
  clinicId: string,
  filters: CashierFilterState,
  detailLimit: number,
): Promise<CashierReportResult> {
  const period = resolveReportPeriod(filters.period, filters.customFrom, filters.customTo);
  const range = toIsoRange(period);
  const { data, error } = await rpc<Record<string, unknown>>('list_cashier_report', {
    p_clinic_id: clinicId,
    p_starts_at: range.start,
    p_ends_at: range.end,
    p_filters: filtersToRpc(filters),
    p_tz: CLINIC_TIMEZONE,
    p_detail_limit: detailLimit,
  });
  if (error) throw error;
  if (!data) {
    return aggregateCashierRows([], period, { clinicName: null });
  }
  return parseCashierReportPayload(data, period);
}

export function useCashierReport(
  clinicId: string | null | undefined,
  filters: CashierFilterState,
  enabled: boolean,
  detailLimit = CASHIER_DETAIL_LIMIT,
) {
  const range = toIsoRange(
    resolveReportPeriod(filters.period, filters.customFrom, filters.customTo),
  );
  return useQuery({
    queryKey: cashierReportQueryKey(clinicId ?? null, range, filters, detailLimit),
    enabled: Boolean(clinicId) && enabled,
    queryFn: async (): Promise<CashierReportResult> => {
      if (!clinicId) {
        return aggregateCashierRows([], resolveReportPeriod(filters.period, filters.customFrom, filters.customTo));
      }
      return fetchCashierReport(clinicId, filters, detailLimit);
    },
    staleTime: 15_000,
  });
}

export function useCashierReportExport(
  clinicId: string | null | undefined,
  filters: CashierFilterState,
) {
  return {
    fetch: async () => {
      if (!clinicId) throw new Error('No active clinic');
      return fetchCashierReport(clinicId, filters, CASHIER_EXPORT_LIMIT);
    },
  };
}

export { parseCashierReportPayload };
